import { ErrorHandler, inject, Injectable, Injector } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import {
  isBrowserNetworkError,
  isPostgrestJwtIssuedAtFutureError,
} from '@packages/shared-utils';

/**
 * Detects chunk-load failures caused by stale chunk hashes after a new deploy.
 *
 * After a deployment, users with a cached HTML shell try to fetch JS chunks
 * under URLs that no longer exist (the hashes changed). This produces one of
 * four observable error signatures:
 *
 *   1. TypeError: Failed to fetch dynamically imported module: …/chunk-XXXXXXXX.js
 *   2. TypeError: 'text/html' is not a valid JavaScript MIME type
 *      (CDN returns the 404 HTML page instead of the missing chunk)
 *   3. TypeError: Importing a module script failed
 *   4. TypeError: error loading dynamically imported module: …/chunk-XXXXXXXX.js
 *
 * We match these signatures conservatively so a legitimate network error
 * does not trigger a reload loop. A single reload is safe: if the error
 * persists after the reload (fresh HTML + fresh chunks), the browser will hit
 * this handler again but, because the new HTML references current chunk hashes,
 * the fetch will succeed and the reload will not recur.
 */
export function isChunkLoadError(error: unknown): boolean {
  return isChunkLoadErrorWithVisited(error, new Set<unknown>());
}

function isChunkLoadErrorWithVisited(error: unknown, visited: Set<unknown>): boolean {
  if ((typeof error === 'object' && error !== null) || typeof error === 'function') {
    if (visited.has(error)) {
      return false;
    }
    visited.add(error);
  }

  const errorLike = error as {
    cause?: unknown;
    ngOriginalError?: unknown;
    originalError?: unknown;
    rejection?: unknown;
  };
  const wrappedErrors = [
    errorLike?.rejection,
    errorLike?.cause,
    errorLike?.ngOriginalError,
    errorLike?.originalError,
  ];
  if (
    wrappedErrors.some(
      (wrappedError) => wrappedError != null && isChunkLoadErrorWithVisited(wrappedError, visited),
    )
  ) {
    return true;
  }

  if (!(error instanceof TypeError) && !(error instanceof Error && error.name === 'TypeError')) {
    return false;
  }

  return hasChunkLoadSignature(error.message ?? '');
}

function hasChunkLoadSignature(message: string): boolean {
  if (!message) {
    return false;
  }

  return (
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('is not a valid JavaScript MIME type') ||
    message.includes('Importing a module script failed') ||
    message.includes('error loading dynamically imported module')
  );
}

/**
 * Global error handler to catch and handle specific errors
 *
 * Note: HttpErrorResponse instances are handled by the HTTP interceptor
 * (http-correlation.interceptor.ts) which applies filtering rules.
 * The GlobalErrorHandler only reports non-HTTP errors to Sentry.
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private injector = inject(Injector);

  handleError(error: unknown): void {
    // Skip HttpErrorResponse — the HTTP interceptor handles those with filtering rules.
    // No console logging here to avoid noise.
    if (error instanceof HttpErrorResponse) {
      return;
    }

    // Recover from chunk-load failures caused by stale chunk hashes after a deploy.
    // A 30-second sessionStorage cooldown prevents an infinite reload loop if chunk
    // loading keeps failing after reload (e.g. bad deploy or CDN cache issue).
    if (isChunkLoadError(error)) {
      const reloadGuardKey = 'brokers-portal:chunk-load-reload-at';
      const reloadCooldownMs = 30_000;
      const lastReloadAt = Number(sessionStorage.getItem(reloadGuardKey) ?? '0');

      if (Date.now() - lastReloadAt > reloadCooldownMs) {
        sessionStorage.setItem(reloadGuardKey, String(Date.now()));
        console.warn(
          'ChunkLoadError detected — reloading to recover from stale chunk hashes.',
          error,
        );
        // Hard navigation with a cache-buster so a stale cached index.html cannot
        // reload the same missing chunk hashes (LEA-199).
        const url = new URL(window.location.href);
        url.searchParams.set('_chunk_reload', String(Date.now()));
        window.location.replace(url.toString());
        return;
      }

      console.warn(
        'ChunkLoadError persisted after a recent reload attempt; forwarding to Sentry.',
        error,
      );
    }

    // Transient browser network failures (offline / abort) — do not open Sentry
    // ERROR issues for Supabase POJO NetworkError captures (LEA-274).
    // Exclude chunk-load signatures: "Failed to fetch dynamically imported module"
    // also matches isBrowserNetworkError, but persistent chunk failures after the
    // reload cooldown must still reach Sentry (LEA-199).
    if (isBrowserNetworkError(error) && !isChunkLoadError(error)) {
      console.warn('Transient browser network error (not reported to Sentry):', error);
      return;
    }

    // Transient PostgREST JWT clock skew (PGRST303) — do not open Sentry ERROR
    // issues for this infrastructure blip (LEA-295; same capture spirit as LEA-274).
    if (isPostgrestJwtIssuedAtFutureError(error)) {
      console.warn(
        'Transient PostgREST PGRST303 JWT clock skew (not reported to Sentry):',
        error,
      );
      return;
    }

    // Send non-HTTP errors to Sentry if initialized
    if (Sentry.isInitialized()) {
      // Get current route (strip query params to avoid PII leakage)
      let routePath = 'no-route';
      try {
        const router = this.injector.get(Router);
        routePath = (router.url || 'no-route').split('?')[0];
      } catch {
        // Router might not be available in all contexts
      }

      // Pass tags directly to captureException to avoid polluting the global scope
      Sentry.captureException(error, { tags: { route: routePath } });
    }

    // Log other errors to console
    console.error('Application Error:', error);
  }
}
